/**
 * Delivery Service
 * Service for managing delivery operations via Commerce Service API
 * Handles delivery creation, updates, filtering, and retrieval
 */

import type {
  Delivery,
  CreateDeliveryRequest,
  UpdateDeliveryRequest,
  DeliveryFilterParams,
  DeliveryListResponse,
} from '@/modules/commerce/delivery/types/delivery';

// ============================================
// CONFIGURATION
// ============================================

const COMMERCE_SERVICE_BASE_URL = import.meta.env.VITE_COMMERCE_SERVICE_URL || '/commerce-service';

const DELIVERY_API_PATH = '/api/v1/deliveries';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem('saubhagya_jwt_token');
  console.log('[DeliveryService] JWT token check:', token ? 'Token exists' : 'No token found');
  return token;
}

/**
 * Validate JWT token exists
 */
function validateAuthToken(): void {
  const token = getAuthToken();
  if (!token) {
    throw new DeliveryApiError(
      'Authentication required. Please login again.',
      401
    );
  }
}

/**
 * Get authorization headers
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
}

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Custom error class for Delivery API errors
 */
class DeliveryApiError extends Error {
  constructor(
    public message: string,
    public statusCode: number,
    public responseData?: any
  ) {
    super(message);
    this.name = 'DeliveryApiError';
  }
}

/**
 * Handle API errors
 */
async function handleApiError(response: Response): Promise<never> {
  let errorMessage = `API Error: ${response.status}`;
  let responseData: any = null;

  try {
    responseData = await response.json();
    errorMessage = responseData.message || responseData.error || errorMessage;
  } catch (e) {
    console.error('[DeliveryService] Failed to parse error response:', e);
  }

  console.error('[DeliveryService] API Error:', {
    status: response.status,
    message: errorMessage,
    data: responseData,
  });

  throw new DeliveryApiError(errorMessage, response.status, responseData);
}

// ============================================
// API OPERATIONS
// ============================================

/**
 * Create a new delivery
 */
export async function createDelivery(
  delivery: CreateDeliveryRequest,
  assignedFrom: string = 'UrjaVyapar'
): Promise<Delivery> {
  try {
    validateAuthToken();

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}?assignedFrom=${encodeURIComponent(assignedFrom)}`;

    console.log('[DeliveryService] Creating delivery:', {
      deliveryData: delivery,
      assignedFrom,
      url,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(delivery),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const createdDelivery = await response.json();
    console.log('[DeliveryService] Delivery created successfully:', createdDelivery);
    return createdDelivery;
  } catch (error) {
    console.error('[DeliveryService] Failed to create delivery:', error);
    throw error;
  }
}

/**
 * Get all deliveries with filters and pagination
 */
export async function listDeliveries(
  filters: DeliveryFilterParams = {},
  page: number = 0,
  size: number = 20
): Promise<DeliveryListResponse> {
  try {
    validateAuthToken();

    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.assignedFrom) params.append('assignedFrom', filters.assignedFrom);
    if (filters.search) params.append('search', filters.search);
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);

    params.append('page', page.toString());
    params.append('size', size.toString());

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}?${params.toString()}`;

    console.log('[DeliveryService] Listing deliveries:', { filters, page, size, url });

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const data = await response.json();
    console.log('[DeliveryService] Deliveries fetched successfully:', data);

    return {
      content: data.content || [],
      totalElements: data.totalElements || 0,
      totalPages: data.totalPages || 0,
      currentPage: data.pageable?.pageNumber || page,
      pageSize: data.pageable?.pageSize || size,
      hasNext: !data.last || false,
      hasPrevious: !data.first || false,
    };
  } catch (error) {
    console.error('[DeliveryService] Failed to list deliveries:', error);
    throw error;
  }
}

/**
 * Get delivery by ID
 */
export async function getDeliveryById(id: number): Promise<Delivery> {
  try {
    validateAuthToken();

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}/${id}`;

    console.log('[DeliveryService] Fetching delivery:', { id, url });

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const delivery = await response.json();
    console.log('[DeliveryService] Delivery fetched successfully:', delivery);
    return delivery;
  } catch (error) {
    console.error('[DeliveryService] Failed to fetch delivery:', error);
    throw error;
  }
}

/**
 * Get deliveries by source (UrjaVyapar or Transport)
 */
export async function getDeliveriesBySource(
  source: 'UrjaVyapar' | 'Transport',
  page: number = 0,
  size: number = 20
): Promise<DeliveryListResponse> {
  try {
    validateAuthToken();

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}/source/${encodeURIComponent(source)}?page=${page}&size=${size}`;

    console.log('[DeliveryService] Fetching deliveries by source:', { source, url });

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const data = await response.json();
    console.log('[DeliveryService] Deliveries by source fetched successfully:', data);

    return {
      content: data.content || [],
      totalElements: data.totalElements || 0,
      totalPages: data.totalPages || 0,
      currentPage: data.pageable?.pageNumber || page,
      pageSize: data.pageable?.pageSize || size,
      hasNext: !data.last || false,
      hasPrevious: !data.first || false,
    };
  } catch (error) {
    console.error('[DeliveryService] Failed to fetch deliveries by source:', error);
    throw error;
  }
}

/**
 * Update delivery details
 */
export async function updateDelivery(
  id: number,
  updates: UpdateDeliveryRequest
): Promise<Delivery> {
  try {
    validateAuthToken();

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}/${id}`;

    console.log('[DeliveryService] Updating delivery:', { id, updates, url });

    const response = await fetch(url, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const updatedDelivery = await response.json();
    console.log('[DeliveryService] Delivery updated successfully:', updatedDelivery);
    return updatedDelivery;
  } catch (error) {
    console.error('[DeliveryService] Failed to update delivery:', error);
    throw error;
  }
}

/**
 * Update delivery status only
 */
export async function updateDeliveryStatus(
  id: number,
  newStatus: string
): Promise<Delivery> {
  try {
    validateAuthToken();

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}/${id}/status`;

    console.log('[DeliveryService] Updating delivery status:', { id, newStatus, url });

    const response = await fetch(url, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify({ status: newStatus }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const updatedDelivery = await response.json();
    console.log('[DeliveryService] Delivery status updated successfully:', updatedDelivery);
    return updatedDelivery;
  } catch (error) {
    console.error('[DeliveryService] Failed to update delivery status:', error);
    throw error;
  }
}

/**
 * Cancel delivery
 */
export async function cancelDelivery(
  id: number,
  reason: string = 'No reason provided'
): Promise<void> {
  try {
    validateAuthToken();

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}/${id}?reason=${encodeURIComponent(reason)}`;

    console.log('[DeliveryService] Cancelling delivery:', { id, reason, url });

    const response = await fetch(url, {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    console.log('[DeliveryService] Delivery cancelled successfully');
  } catch (error) {
    console.error('[DeliveryService] Failed to cancel delivery:', error);
    throw error;
  }
}

/**
 * Get delivery statistics by source
 */
export async function getDeliveryStatsBySource(): Promise<Record<string, number>> {
  try {
    validateAuthToken();

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}/stats/by-source`;

    console.log('[DeliveryService] Fetching stats by source:', { url });

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const stats = await response.json();
    console.log('[DeliveryService] Stats by source fetched successfully:', stats);
    return stats;
  } catch (error) {
    console.error('[DeliveryService] Failed to fetch stats by source:', error);
    throw error;
  }
}

/**
 * Get delivery statistics by status
 */
export async function getDeliveryStatsByStatus(): Promise<Record<string, number>> {
  try {
    validateAuthToken();

    const url = `${COMMERCE_SERVICE_BASE_URL}${DELIVERY_API_PATH}/stats/by-status`;

    console.log('[DeliveryService] Fetching stats by status:', { url });

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const stats = await response.json();
    console.log('[DeliveryService] Stats by status fetched successfully:', stats);
    return stats;
  } catch (error) {
    console.error('[DeliveryService] Failed to fetch stats by status:', error);
    throw error;
  }
}

export { DeliveryApiError };
